<template>
    <div class="flex flex-column xl:flex-row xl:align-items-center justify-content-between w-full gap-3">
        <div>
            <Inplace
                v-if="!isDemo"
                ref="inplace"
                :pt="{
                    root: {class: 'align-self-start'},
                    display: {class: 'inline-block', style: 'margin-left: -12px;'}
                }"
            >
                <template #display>
                    <div class="flex align-items-center gap-2">
                        <h3 :title="getName">{{ getName }}</h3>
                        <i class="pi pi-pencil group_report_title__icon"></i>
                    </div>
                </template>
                <template #content>
                    <div class="flex align-items-center gap-2" :class="{'p-disabled': this.loading}">
                        <InputText
                            v-model="model.name"
                            @keyup.enter="onRenameProject"
                            @keyup.esc="onCloseRename"
                            @focusout="onCloseRename"
                            autofocus
                            class="group_report_title__input"
                        />
                        <i class="pi pi-pencil group_report_title__icon"></i>
                    </div>
                </template>
            </Inplace>
            <h3 v-else :title="getName" class="align-self-start">{{ getName }}</h3>
            <span class="text-color-secondary">{{ getDbCaption }} — {{ label }}</span>
        </div>
        <div v-if="!isDemo" class="flex-wrap flex flex-column sm:flex-row gap-2 w-full sm:w-auto">
            <Button
                @click="onCreateCluster"
                :label="$t('lang.group_report.create_cluster_label')"
                :title="$t('lang.group_report.create_cluster_title')"
                size="small"
            />
            <Button
                :label="$t('lang.group_report.added_keys')"
                size="small"
                icon="pi pi-angle-down"
                icon-pos="right"
                @click="onToggleAdd"
            />
            <Button
                :label="$t('lang.group_report.delete_keys')"
                size="small"
                icon="pi pi-angle-down"
                icon-pos="right"
                @click="onToggleDelete"
            />
            <Button
                v-if="isKeyModify && isShowRecalcBtn"
                :label="$t('lang.group_report.recalc_conc')"
                size="small"
                @click="onRecalcConcurents"
            />
            <TieredMenu
                ref="tieredMenuAdd"
                :model="tieredMenu.add"
                popup
                :pt="{
                    root: {style: 'z-index: 100000;'}
                }"
            />
            <TieredMenu
                ref="tieredMenuDelete"
                :model="tieredMenu.delete"
                popup
                :pt="{
                    root: {style: 'z-index: 100000;'}
                }"
            />
        </div>
        <!-- импорт -->
        <KSFromTextArea
            :target="target"
            :report-rid="reportUid"
            :type="'import'"
            :name="getListenerImportFromAreaOpenId"
        />
        <KSFromFile
            :target="target"
            :report-rid="reportUid"
            :type="'import'"
            :name="getListenerImportFromFileOpenId"
        />
        <KSFromDomains
            :target="target"
            :report-rid="reportUid"
            :type="'import'"
            :config="config"
            :name="getListenerImportFromDomainsOpenId"
        />
        <!-- удаление -->
        <KSFromTextArea
            :target="target"
            :report-rid="reportUid"
            :type="'delete'"
            :name="getListenerDeleteFromAreaOpenId"
        />
        <KSFromFile
            :target="target"
            :report-rid="reportUid"
            :type="'delete'"
            :name="getListenerDeleteFromFileOpenId"
        />
        <KSFromDomains
            :target="target"
            :report-rid="reportUid"
            :type="'delete'"
            :config="config"
            :name="getListenerDeleteFromDomainsOpenId"
        />
        <KSFromFilter
            :target="target"
            :report-rid="reportUid"
            :type="'delete'"
            :config="config"
            :name="getListenerDeleteFromFilterOpenId"
        />
        <KSDeleteDoubles
            :target="target"
            :report-rid="reportUid"
            :type="'delete'"
            :name="getListenerDeleteDoublesOpenId"
        />
        <KSFromTemplate
            :target="target"
            :report-rid="reportUid"
            :type="'delete'"
            :name="getListenerDeleteFromTemplateOpenId"
        />
    </div>
</template>

<script>
import Inplace from 'primevue/inplace';
import InputText from "primevue/inputtext";
import GroupReportApi from "../../../api/GroupReportApi";
import Alerts from "../../../mixins/Alerts";
import Button from "primevue/button";
import TieredMenu from "primevue/tieredmenu";
import {Listeners} from "../../../constants/listeners";
import KSFromTextArea from "./KSFromTextArea.vue";
import KSFromFile from "./KSFromFile.vue";
import KSFromDomains from "./KSFromDomains.vue";
import KSFromFilter from "./KSFromFilter.vue";
import KSDeleteDoubles from "./KSDeleteDoubles.vue";
import KSFromTemplate from "./KSFromTemplate.vue";
import {ArrayHelper} from "../../../helpers/ArrayHelper";

export default {
    name: "GroupReportTitle",
    mixins: [Alerts],
    props: {
        reportName: {
            type: String,
            required: true,
        },
        reportUid: {
            type: String,
            required: true,
        },
        reportDb: {
            type: String,
            required: true,
        },
        isDemo: {
            type: Boolean,
            required: true,
        },
        // тип отчёта
        target: {
            type: String,
            enum: ['organic', 'context', 'all'],
            required: true,
        },
        isKeyModify: {
            type: Boolean,
            required: true,
        },
        // перерасчёт конкурентов
        isShowRecalcBtn: {
            type: Boolean,
            required: false,
            default: false,
        },
        // название отчёта
        label: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            model: {
                name: this.reportName,
            },
            currentName: this.reportName,
            loading: false,
            config: {},
            tieredMenu: {
                add:  [
                    {
                        label: this.$t('lang.group_report.gd_from_area_1'),
                        command: () => this.showComponent(this.getListenerImportFromAreaOpenId)
                    },
                    {
                        label: this.$t('lang.group_report.gd_from_file'),
                        command: () => this.showComponent(this.getListenerImportFromFileOpenId)
                    },
                    {
                        label: this.$t('lang.group_report.gd_from_domain_1'),
                        command: () => this.showComponent(this.getListenerImportFromDomainsOpenId)
                    },
                ],
                delete: [
                    {
                        label: this.$t('lang.group_report.gd_from_area'),
                        command: () => this.showComponent(this.getListenerDeleteFromAreaOpenId)
                    },{
                        label: this.$t('lang.group_report.gd_from_file'),
                        command: () => this.showComponent(this.getListenerDeleteFromFileOpenId)
                    },{
                        label: this.$t('lang.group_report.gd_from_domain'),
                        command: () => this.showComponent(this.getListenerDeleteFromDomainsOpenId)
                    },{
                        label: this.$t('lang.group_report.gd_from_filter'),
                        command: () => this.showComponent(this.getListenerDeleteFromFilterOpenId)
                    },{
                        label: this.$t('lang.group_report.gd_from_double'),
                        command: () => this.showComponent(this.getListenerDeleteDoublesOpenId)
                    },{
                        label: this.$t('lang.group_report.gd_from_tmpl'),
                        command: () => this.showComponent(this.getListenerDeleteFromTemplateOpenId)
                    },
                ]
            }
        };
    },
    mounted() {
        this.$emitter.on(Listeners.CLUSTER_SHOW_COMPONENT, eventData => this.onShowComponent(eventData));
    },
    computed: {
        getName() {
            return this.currentName;
        },
        getListenerImportFromAreaOpenId() {
            return Listeners.GROUP_POPUP_IMPORT_FROM_AREA_OPEN;
        },
        getListenerImportFromFileOpenId() {
            return Listeners.GROUP_POPUP_IMPORT_FROM_FILE_OPEN;
        },
        getListenerImportFromDomainsOpenId() {
            return Listeners.GROUP_POPUP_IMPORT_FROM_DOMAINS_OPEN;
        },
        getListenerDeleteFromAreaOpenId() {
            return Listeners.GROUP_POPUP_DELETE_FROM_AREA_OPEN;
        },
        getListenerDeleteFromFileOpenId() {
            return Listeners.GROUP_POPUP_DELETE_FROM_FILE_OPEN;
        },
        getListenerDeleteFromDomainsOpenId() {
            return Listeners.GROUP_POPUP_DELETE_FROM_DOMAINS_OPEN;
        },
        getListenerDeleteFromFilterOpenId() {
            return Listeners.GROUP_POPUP_DELETE_FROM_FILTER_OPEN;
        },
        getListenerDeleteDoublesOpenId() {
            return Listeners.GROUP_POPUP_DELETE_DOUBLES_OPEN;
        },
        getListenerDeleteFromTemplateOpenId() {
            return Listeners.GROUP_POPUP_DELETE_FROM_TEMPLATE_OPEN;
        },
        getDbCaption() {
            const region = ArrayHelper.getObjectByAttrValue(
                this.$tm('lang.main.bases'),
                'name',
                this.reportDb
            );

            return region?.caption;
        },
    },
    methods: {
        onRenameProject() {
            this.loading = true;
            GroupReportApi
                .renameReport(this.reportUid, this.model)
                .then(() => {
                    this.currentName = this.model.name;
                    this.$refs.inplace.close();
                })
                .catch(error => this.alertError(error))
                .finally(() => this.loading = false);
        },
        onCloseRename() {
            this.$refs.inplace.close();
            this.model.name = this.currentName;
        },
        showComponent(name) {
            this.$emitter.emit(name);
        },
        onShowComponent(event) {
            this.config.ids = event.ids;
            this.showComponent(event.component);
        },
        onCreateCluster() {
            const vars = {
                from: 'gkeywords',
                rid: this.reportUid
            };

            window.open('/' + window.locale + '/clustering-list#' + Object.keys(vars).map(key => `${key}=${encodeURIComponent(vars[key])}`).join('&'), '_blank');
        },
        onRecalcConcurents() {
            this.loading = true;
            GroupReportApi
                .recalcConcurents(this.reportUid)
                .then(() => window.location.reload())
                .catch(error => this.alertError(error))
                .finally(() => this.loading = false);
        },
        onToggleAdd(event) {
            this.$refs.tieredMenuAdd.toggle(event);
        },
        onToggleDelete(event) {
            this.$refs.tieredMenuDelete.toggle(event);
        },
    },
    components: {
        KSFromTextArea,
        KSFromFile,
        KSFromDomains,
        KSFromFilter,
        KSDeleteDoubles,
        KSFromTemplate,
        TieredMenu,
        Button,
        InputText,
        Inplace,
    },
}
</script>

<style scoped>
.group_report_title__input {
    font-size: 24px;
    margin: 1px 0 0;
}
.group_report_title__icon {
    color: var(--secondary-color);
}
</style>
